/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Alert, InputRef } from 'antd'
import type { BaseSelectRef } from 'rc-select'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TextInput } from 'src/components/ts-components/TextInput'
import Text from 'src/components/ts-components/Text'
import ModernPhoneInput, {
  ModernPhoneInputRef,
} from 'src/components/ts-components/ModernPhoneInput'
import CopyComponent from 'src/components/CopyComponent'
import useWindowSize from 'src/hooks/useWindowSize'
import { useOfficialContext } from './OfficialContext'
import RoleSelect from './RoleSelect'
import DeleteOfficialModal from './DeleteOfficialModal'

// Field order for focus priority (matches display order)
const FIELD_ORDER = [
  'name_first',
  'name_last',
  'official_type_id',
  'email',
  'phone_number',
] as const

const fieldWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DeleteButton = ({ onClick }: { onClick?: () => void }) => {
  return (
    <button
      type='button'
      onClick={onClick}
      css={css`
        display: flex;
        padding: 8px 16px 8px 8px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        border-radius: 8px;
        border: 1px solid ${colors.ERROR_200};
        background: ${colors.ERROR_200};
        box-shadow: 0px 2px 0px 0px rgba(5, 145, 255, 0.1);
        cursor: pointer;
        &:hover {
          opacity: 0.9;
        }
      `}
    >
      <DeleteOutlined
        css={css`
          font-size: 24px;
          color: ${colors.NEUTRAL_0};
        `}
        width={24}
        height={24}
      />
      <Text variant='heading6' weight='semiBold' color={colors.NEUTRAL_0}>
        REMOVE OFFICIAL
      </Text>
    </button>
  )
}

const OfficialModalContent: React.FC = () => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { state, dispatch, isEdit, onDelete, isSoccer, isOrg, sportId } =
    useOfficialContext()

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Refs for focusable fields
  const nameFirstRef = useRef<InputRef>(null)
  const nameLastRef = useRef<InputRef>(null)
  const officialTypeRef = useRef<BaseSelectRef>(null)
  const emailRef = useRef<InputRef>(null)
  const phoneRef = useRef<ModernPhoneInputRef>(null)

  // Focus first error field when errors change
  useEffect(() => {
    if (state.fieldErrors.length > 0) {
      const fieldRefs: Record<
        string,
        React.RefObject<{ focus?: () => void } | null>
      > = {
        name_first: nameFirstRef,
        name_last: nameLastRef,
        official_type_id: officialTypeRef,
        email: emailRef,
        phone_number: phoneRef,
      }
      const firstErrorField = FIELD_ORDER.find((field) =>
        state.fieldErrors.includes(field)
      )
      if (firstErrorField) {
        const ref = fieldRefs[firstErrorField]
        ref?.current?.focus?.()
      }
    }
  }, [state.fieldErrors])

  const handleFieldChange = useCallback(
    (field: string) => (value: string) => {
      dispatch({
        type: 'SET_FIELD',
        field: field as keyof typeof state,
        value,
      })
    },
    [dispatch]
  )

  const handleRoleChange = useCallback(
    (value: number) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'official_type_id',
        value,
      })
    },
    [dispatch]
  )

  const handlePhoneChange = useCallback(
    (value: string | undefined) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'phone_number',
        value: value || '',
      })
    },
    [dispatch]
  )

  const handlePhoneCountryChange = useCallback(
    (country: string | undefined) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'phone_number_country',
        value: country || 'US',
      })
    },
    [dispatch]
  )

  const handleDeleteClick = useCallback(() => {
    setDeleteError(null)
    setIsDeleteModalOpen(true)
  }, [])

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false)
    setDeleteError(null)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    try {
      setIsDeleting(true)
      setDeleteError(null)
      await onDelete()
      setIsDeleteModalOpen(false)
    } catch (e: any) {
      setDeleteError(e?.message || 'Failed to delete official')
    } finally {
      setIsDeleting(false)
    }
  }, [onDelete])

  const hasExistingEmail = isEdit && state.hasInitialEmail

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 16px;
        height: 100%;
        overflow-y: auto;
        width: 100%;
      `}
    >
      {/* Header with ID */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Official Info
        </Text>
        {isEdit && state.id ? (
          <div>
            <CopyComponent id={state.id} />
          </div>
        ) : null}
      </div>

      {/* Name Fields */}
      <div
        css={css`
          display: flex;
          gap: 16px;
          ${isMobile &&
          css`
            flex-direction: column;
          `}
        `}
      >
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            ref={nameFirstRef}
            name='name_first'
            label='First Name'
            value={state.name_first}
            setValue={handleFieldChange('name_first')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            placeholder='First Name'
            required
          />
        </div>
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            ref={nameLastRef}
            name='name_last'
            label='Last Name'
            value={state.name_last}
            setValue={handleFieldChange('name_last')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            placeholder='Last Name'
            required
          />
        </div>
      </div>

      {/* Role Selection */}
      <div css={fieldWrapperStyle}>
        <RoleSelect
          ref={officialTypeRef}
          name='official_type_id'
          value={state.official_type_id}
          onChange={handleRoleChange}
          isSoccer={isSoccer}
          isOrg={isOrg}
          sportId={sportId}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          required
        />
      </div>

      {/* Separator */}
      <div
        css={css`
          height: 1px;
          background-color: ${colors.SOFT_STEEL};
          width: 100%;
        `}
      />

      {/* Contact Info Header */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Contact Info
        </Text>
      </div>

      {/* Email and Phone */}
      <div
        css={css`
          display: flex;
          gap: 16px;
          ${isMobile &&
          css`
            flex-direction: column;
          `}
        `}
      >
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            ref={emailRef}
            name='email'
            label='Email'
            value={state.email}
            setValue={handleFieldChange('email')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            disabled={Boolean(hasExistingEmail)}
            placeholder='Email Address'
            type='email'
          />
        </div>
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <ModernPhoneInput
            ref={phoneRef}
            value={state.phone_number}
            onChange={handlePhoneChange}
            onCountryChange={handlePhoneCountryChange}
            label='Phone Number'
            error={
              state.fieldErrors?.includes('phone_number')
                ? state.customError?.phone_number || 'Invalid phone number'
                : ''
            }
            validateOnBlur={false}
          />
        </div>
      </div>

      {/* Message Field (only for create mode) */}
      {!isEdit && (
        <div css={[fieldWrapperStyle, { display: 'none' }]}>
          <TextInput
            name='message'
            label='Invitation Message'
            value={state.message}
            setValue={handleFieldChange('message')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            placeholder='Add A Custom Message To The Invitation Email'
          />
        </div>
      )}

      {/* Warning Message */}
      {!isEdit && state.email && (
        <Alert
          message={
            <Text
              variant='paragraphSmall'
              weight='regular'
              style={'width: 100%; margin-left: 8px;'}
            >
              Including An Email Address Will Send An Invitation To This User
            </Text>
          }
          type='warning'
          showIcon
          icon={
            <InfoCircleOutlined
              css={css`
                font-size: 20px;
                color: ${colors.WARNING_200};
              `}
            />
          }
          css={css`
            width: 100%;
            border-radius: 8px;
          `}
        />
      )}

      {/* Delete Section (only for edit mode) */}
      {isEdit && (
        <>
          <div
            css={css`
              height: 1px;
              background-color: ${colors.SOFT_STEEL};
              width: 100%;
              margin-top: 8px;
            `}
          />
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
              width: 100%;
            `}
          >
            <Text
              variant='captionSmall'
              weight='semiBold'
              color={colors.NEUTRAL_50}
            >
              Danger Zone
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.WHITE}
            >
              Permanently Remove This Official From The Organization.
            </Text>
            <DeleteButton onClick={handleDeleteClick} />
          </div>
        </>
      )}

      {/* Error Display */}
      {state.error && (
        <Alert
          message={state.error}
          type='error'
          showIcon
          css={css`
            background: ${colors.ERROR_50};
            border: 1px solid ${colors.ERROR_300};
            border-radius: 8px;
            margin-top: 8px;
            .ant-alert-message {
              color: ${colors.ERROR_300};
            }
            .ant-alert-icon {
              color: ${colors.ERROR_300};
            }
          `}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteOfficialModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        officialName={`${state.name_first} ${state.name_last}`}
        isLoading={isDeleting}
        error={deleteError}
        title='Remove Official'
      />
    </div>
  )
}

export default OfficialModalContent
